import './FormHistoryModal.scss';

import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import Map from '../../components/Map';
import ErrorBoundary from '../../components/ErrorBoundary';
import { apimCall } from '../../helpers/api';
import { getDateTimeFormat } from '../../helpers/DateTimeUtility';
import { useApim } from '../../hooks/useApi';
import { GoFormzServices } from '../../AppSettings';
import GfModal from '../../gfcomponents/GfModal/GfModal';
import { AppFeatures, canIUse, Features } from '../../helpers/PermissionUtility';
import { FormVersion, FormAndTemplate } from '../../types/form-types';
import { useAppSelector } from '../../hooks/ReduxHooks';
import { EditionCode } from '../../subscription/subscription-types';
import { analyticsTrack } from '../../helpers/AnalyticsUtility';
import { CommentThread } from '../forms-types';

export interface FormHistoryModalProps {
  name?: string;
  id?: string;
  latitude?: number;
  longitude?: number;
  onClose?: VoidFunction;
  onRevertToVersion?: (formId: string, versionId: string) => void;
  formAndTemplate?: unknown;
  open?: boolean;
}

export default function FormHistoryModal(props: FormHistoryModalProps) {
  const [versions, versionsLoading, versionsLoaded] = useApim<FormVersion[]>(GoFormzServices.TemplateService, `/forms/${props.id}/versions`);
  const [comments, commentsLoading, commentsLoaded] = useApim<CommentThread[]>(GoFormzServices.AuditService, `/threads/form/${props.id}`);

  const [formAndTemplate, setFormAndTemplate] = useState<FormAndTemplate>({});
  const [loading, setLoading] = useState(false);
  const isAdmin = useAppSelector((state) => state.profile.isAdmin);

  useEffect(() => {
    async function fetchFormAndTemplate() {
      try {
        setLoading(true);
        if (props.formAndTemplate === undefined) {
          const response = await apimCall(GoFormzServices.TemplateService, `/formAndTemplate/${props.id}`);
          setFormAndTemplate(response.data);
        } else {
          setFormAndTemplate(props.formAndTemplate);
        }
      } finally {
        setLoading(false);
      }
    }
    fetchFormAndTemplate();
  }, []);

  const getUserName = (id) => (formAndTemplate.userMap ? formAndTemplate.userMap[id] : null);
  const calendarOptions = {
    sameElse: getDateTimeFormat()
  };

  let templateHeader = null;
  let historyBody: React.Component = null;

  if (formAndTemplate && formAndTemplate.form && versionsLoaded && commentsLoaded) {
    if (versions && versions.length > 0) {
      // @ts-ignore - FIX
      historyBody = (
        // @ts-ignore - FIX
        <VersionHistory
          isAdmin={isAdmin}
          formAndTemplate={formAndTemplate}
          versions={versions}
          getUserName={getUserName}
          calendarOptions={calendarOptions}
          onRevertToVersion={(versionId: string) => props.onRevertToVersion(props.id, versionId)}
          comments={comments}
        />
      );

      if (versions[0].templateName && versions[0].templateVersionNumber) {
        templateHeader = (
          <p>
            Template: {versions[0].templateName} (Version {versions[0].templateVersionNumber})
          </p>
        );
      }
    } else {
      // @ts-ignore - FIX
      historyBody = <LegacyHistory formAndTemplate={formAndTemplate} getUserName={getUserName} calendarOptions={calendarOptions} />;
    }
  }

  return (
    <GfModal className="form-history" title="Form History" onClose={props.onClose} scrolling semanticUiModalProps={{ size: 'small' }}>
      {templateHeader}
      {loading || versionsLoading || commentsLoading ? <div className="ui active loader right-aligned" /> : null}
      <div className="history-items">{historyBody}</div>
    </GfModal>
  );
}

// TODO - GD-20719 web app performance, cleanup below

function getVersionActions(version, previousVersion, formAndTemplate) {
  const actions = [];

  let previousVersionStatus = null;
  let previousVersionOwnerId = null;

  if (version.missingPriorVersions) {
    previousVersionStatus = formAndTemplate.form.completedDate ? 1 : 0;
    previousVersionOwnerId = formAndTemplate.form.userId || formAndTemplate.form.groupId;
  } else if (previousVersion !== null) {
    previousVersionStatus = previousVersion.status;
    previousVersionOwnerId = previousVersion.ownerId;
  }

  if (previousVersion === null && !version.missingPriorVersions) {
    // first version
    if (version.copiedForm) {
      actions.push('duplicated');
    } else {
      actions.push('created');
    }

    if (version.status === 1) {
      actions.push('completed');
    }

    // first version was created and transferred while offline on mobile
    if (version.createdBy !== version.ownerId) {
      actions.push('transferred');
    }
  } else {
    actions.push('modified');

    if (previousVersionStatus === 0 && version.status === 1) {
      actions.push('completed');
    } else if (previousVersionStatus === 1 && version.status === 0) {
      actions.push('reopened');
    }

    if (previousVersionOwnerId !== version.ownerId) {
      if (version.ownerId === version.createdBy) {
        actions.push('takeownership');
      } else {
        actions.push('transferred');
      }
    }
    if (version.publicFormSubmission) {
      actions.push('publicFormSubmission');
    }

    if (previousVersion && previousVersion.templateVersionId !== version.templateVersionId) {
      actions.push('templateVersionChanged');
    }

    if (version.esigned) {
      actions.push('esigned');
    }
  }

  return actions;
}

function getVersionActionTitle(version, actions, template, isAdmin) {
  const safeName = (name) => (name ? name : 'Unknown User');
  const workflowLink = (version.createdByName === 'Workflow' && isAdmin && version.workflowJobId) ?? false;
  const templateLink = canIUse(AppFeatures.TemplateList) ?? false;
  if (actions.indexOf('completed') > -1 && actions.indexOf('created') > -1 && !version.templateVersionNumber) {
    return (
      <span>
        Created and completed from Template {templateLink ? <a href={`/templates/${template.id}`}>{`'${template.name}'`}</a> : `'${template.name}'`} by{' '}
        {safeName(version.createdByName)}
      </span>
    );
  } else if (actions.indexOf('completed') > -1 && actions.indexOf('created') > -1) {
    return (
      <span>
        Created and completed from Template{' '}
        {templateLink ? (
          <a href={`/templates/${template.id}?versionId=${version.templateVersionId}`}>
            {`'${template.name}`} (Version {version.templateVersionNumber})'
          </a>
        ) : (
          `${`'${template.name}`} (Version ${version.templateVersionNumber})'`
        )}{' '}
        by {safeName(version.createdByName)}
      </span>
    );
  } else if (actions.indexOf('transferred') > -1 && actions.indexOf('created') > -1 && !version.templateVersionNumber) {
    return (
      <span>
        Created by {workflowLink ? <a href={`/workflows/jobs/${version.workflowJobId}`}>{safeName(version.createdByName)}</a> : safeName(version.createdByName)}{' '}
        and transferred to {version.ownerType === 2 ? `Group '${safeName(version.ownerName)}'` : safeName(version.ownerName)} from Template{' '}
        {templateLink ? <a href={`/templates/${template.id}`}>{`'${template.name}'`}</a> : `'${template.name}'`}
      </span>
    );
  } else if (actions.indexOf('transferred') > -1 && actions.indexOf('created') > -1) {
    return (
      <span>
        Created by {workflowLink ? <a href={`/workflows/jobs/${version.workflowJobId}`}>{safeName(version.createdByName)}</a> : safeName(version.createdByName)}{' '}
        and transferred to {version.ownerType === 2 ? `Group '${safeName(version.ownerName)}'` : safeName(version.ownerName)} from Template{' '}
        {templateLink ? (
          <a href={`/templates/${template.id}?versionId=${version.templateVersionId}`}>
            {`'${template.name}`} (Version {version.templateVersionNumber}')
          </a>
        ) : (
          `${`'${template.name}`} (Version ${version.templateVersionNumber})'`
        )}
      </span>
    );
  } else if (actions.indexOf('reopened') > -1 && actions.indexOf('transferred') > -1 && workflowLink) {
    return (
      <span>
        Reopened and transferred to {version.ownerType === 2 ? `Group '${safeName(version.ownerName)}'` : safeName(version.ownerName)} by{' '}
        <a href={`/workflows/jobs/${version.workflowJobId}`}>{safeName(version.createdByName)}</a>
      </span>
    );
  } else if (actions.indexOf('reopened') > -1 && actions.indexOf('transferred') > -1) {
    return `Reopened and transferred to ${version.ownerType === 2 ? `Group '${safeName(version.ownerName)}'` : safeName(version.ownerName)}`;
  } else if (actions.indexOf('completed') > -1) {
    return `Completed by ${safeName(version.createdByName)}`;
  } else if (version.publicFormSubmission && version.versionNumber === 1) {
    return (
      <span>
        Created using a Public Form from Template '
        {templateLink ? (
          <a href={`/templates/${template.id}`}>{`${template.name} (Version ${version.templateVersionNumber})'`}</a>
        ) : (
          `'${template.name} (Version ${version.templateVersionNumber})'`
        )}{' '}
        - Owned by {safeName(version.createdByName)}
      </span>
    );
  } else if (version.publicFormSubmission) {
    const identifyingText =
      version.publicFormRecipient?.name && (version.publicFormRecipient?.phoneNumber || version.publicFormRecipient?.email)
        ? ` by ${version.publicFormRecipient.name} (${version.publicFormRecipient.phoneNumber || version.publicFormRecipient.email})`
        : '';
    return `Modified using a Public Form${identifyingText}`;
  } else if (actions.indexOf('created') > -1 && !version.templateVersionNumber) {
    return (
      <span>
        Created from Template {templateLink ? <a href={`/templates/${template.id}`}>{`'${template.name}'`}</a> : `'${template.name}'`} by{' '}
        {workflowLink ? <a href={`/workflows/jobs/${version.workflowJobId}`}>{safeName(version.createdByName)}</a> : safeName(version.createdByName)}
      </span>
    );
  } else if (actions.indexOf('created') > -1) {
    return (
      <span>
        Created from Template{' '}
        {templateLink ? (
          <a href={`/templates/${template.id}?versionId=${version.templateVersionId}`}>
            {`'${template.name}`} (Version {version.templateVersionNumber})'
          </a>
        ) : (
          `${`'${template.name}`} (Version ${version.templateVersionNumber})'`
        )}{' '}
        by {safeName(version.createdByName)}
      </span>
    );
  } else if (actions.indexOf('reopened') > -1) {
    return (
      <span>
        Reopened by{' '}
        {workflowLink ? <a href={`/workflows/jobs/${version.workflowJobId}`}>{safeName(version.createdByName)}</a> : safeName(version.createdByName)}
      </span>
    );
  } else if (actions.indexOf('transferred') > -1) {
    return `Transferred to ${version.ownerType === 2 ? `Group '${safeName(version.ownerName)}'` : safeName(version.ownerName)}`;
  } else if (actions.indexOf('takeownership') > -1) {
    return `Ownership taken by ${version.ownerType === 2 ? `Group '${safeName(version.ownerName)}'` : safeName(version.ownerName)}`;
  } else if (actions.indexOf('templateVersionChanged') > -1) {
    return `Updated to Template Version ${version.templateVersionNumber} by ${safeName(version.createdByName)}`;
  } else if (actions.indexOf('duplicated') > -1) {
    return `Duplicated from ${version.copiedForm.name} by ${safeName(version.createdByName)}`;
  }
  return (
    <span>
      Modified by {workflowLink ? <a href={`/workflows/jobs/${version.workflowJobId}`}>{safeName(version.createdByName)}</a> : safeName(version.createdByName)}
    </span>
  );
}

function getVersionIcon(actions) {
  if (actions.indexOf('completed') > -1) {
    return 'check circle outline';
  } else if (actions.indexOf('publicFormSubmission') > -1) {
    return 'file alternate outline';
  } else if (actions.indexOf('transferred') > -1 || actions.indexOf('takeownership') > -1) {
    return 'arrow alternate circle right outline';
  } else if (actions.indexOf('created') > -1) {
    return 'file text outline';
  } else if (actions.indexOf('reopened') > -1) {
    return 'undo';
  } else if (actions.indexOf('duplicated') > -1) {
    return 'copy outline';
  }
  return 'file alternate outline';
}

export interface VersionHistoryProps {
  isAdmin: boolean;
  formAndTemplate: FormAndTemplate;
  versions: FormVersion[];
  getUserName: (id: string) => string;
  calendarOptions: {
    sameElse: string;
  };
  onRevertToVersion: (versionId: string) => void;
  comments?: CommentThread[];
}
function VersionHistory(props: VersionHistoryProps) {
  const { isAdmin, formAndTemplate, versions, getUserName, calendarOptions, comments } = props;
  const [expandedVersionGroup, setExpandedVersionGroup] = useState();
  const [showMapVersionId, setShowMapVersionId] = useState();
  const [showCommentVersionIds, setShowCommentVersionIds] = useState<string[]>([]);
  const subscription = useAppSelector<EditionCode>((state) => state.profile.client.subscription);

  const historyItems = [];
  const versionsGroupedByActions = [];
  const latestFormVersion = versions[versions.length - 1];
  for (let i = 0; i < versions.length; i++) {
    const version = versions[i];
    const previousVersion = i > 0 ? versions[i - 1] : null;
    const versionActions = getVersionActions(version, previousVersion, formAndTemplate);

    const previousVersionsGroupedByAction = versionsGroupedByActions.length > 0 ? versionsGroupedByActions[versionsGroupedByActions.length - 1] : null;
    if (
      previousVersionsGroupedByAction &&
      previousVersionsGroupedByAction.versionActions.length === 1 &&
      previousVersionsGroupedByAction.versionActions[0] === 'modified' &&
      versionActions.length === 1 &&
      versionActions[0] === 'modified' &&
      previousVersionsGroupedByAction.versions[0].createdBy === version.createdBy &&
      previousVersionsGroupedByAction.versions[0].source === version.source
    ) {
      previousVersionsGroupedByAction.versions.push(version);
    } else {
      versionsGroupedByActions.push({
        groupId: version.versionId,
        versionActions,
        versions: [version]
      });
    }
  }

  for (const versionsGroupedByAction of versionsGroupedByActions) {
    const { versionActions } = versionsGroupedByAction;
    const groupedVersions = versionsGroupedByAction.versions;
    const firstVersion = groupedVersions[0];
    const lastVersion = groupedVersions[groupedVersions.length - 1];

    historyItems.push(
      <React.Fragment key={firstVersion.versionId}>
        {firstVersion.missingPriorVersions ? (
          <React.Fragment>
            <div key="created" className="history-item-row">
              <div className="history-item">
                <Icon name="file text outline" />
                <div className="item-content">
                  <div className="item-header">
                    Created from {formAndTemplate.template.name} (Version {firstVersion.templateVersionNumber}) by {getUserName(formAndTemplate.form.createdBy)}
                  </div>
                  <div className="item-description">{moment.utc(formAndTemplate.form.createdDate).local().calendar(null, calendarOptions)}</div>
                </div>
              </div>
            </div>
            {getMergedTransferHistory(formAndTemplate, versionsGroupedByActions).map((item, i) => (
              <div key={i} className="history-item-row">
                <div className="history-item">
                  <Icon name="arrow alternate circle right outline" />
                  <div className="item-content">
                    <div className="item-header">Transferred to {getUserName(item.destId)}</div>
                    <div className="item-description">{moment.utc(item.date).local().calendar(null, calendarOptions)}</div>
                  </div>
                </div>
              </div>
            ))}
          </React.Fragment>
        ) : null}
        <div className="history-item-row">
          <div className="history-item">
            <Icon name={getVersionIcon(versionActions)} />
            <div className="item-content">
              <div className="item-header">{getVersionActionTitle(firstVersion, versionActions, formAndTemplate.template, isAdmin)}</div>
              <div className="item-description">
                <span>{moment.utc(lastVersion.createdDate).local().calendar(null, calendarOptions)}</span>
                {firstVersion.source ? <span>{getSourceDisplay(firstVersion.source)}</span> : null}
                <span>-</span>
                {groupedVersions.length > 1 ? (
                  <React.Fragment>
                    <span>{groupedVersions.length} versions -</span>
                    <button
                      className="link-button"
                      onClick={() => setExpandedVersionGroup(expandedVersionGroup === versionsGroupedByAction.groupId ? null : versionsGroupedByAction.groupId)}
                    >
                      {expandedVersionGroup === versionsGroupedByAction.groupId ? 'Collapse' : 'Expand'}
                    </button>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Link to={`/forms/${firstVersion.formId}?versionId=${firstVersion.versionId}`} target="_blank">
                      Form Version {firstVersion.versionNumber}
                    </Link>
                    {firstVersion.esigned ? ' - eSigned' : ''}
                  </React.Fragment>
                )}
              </div>
            </div>
            <div className="history-item-row-right-container">
              {firstVersion.location && (
                <div
                  className="map-icon"
                  onClick={() => {
                    setShowMapVersionId(showMapVersionId === firstVersion.versionId ? '' : firstVersion.versionId);
                  }}
                >
                  <Icon name={'map outline'} />
                </div>
              )}
              {(groupedVersions.length ?? 0) <= 1 && (
                <RevertVersion formVersion={firstVersion} latestFormVersion={latestFormVersion} onRevertToVersion={props?.onRevertToVersion} />
              )}
              {!!comments && comments.some((x) => x.resourceId === firstVersion.versionId) && (
                <ShowComment formVersion={firstVersion} showCommentVersionIds={showCommentVersionIds} setShowCommentVersionIds={setShowCommentVersionIds} />
              )}
            </div>
          </div>
          {showMapVersionId === firstVersion.versionId ? (
            <div className="map-wrapper">
              <ErrorBoundary errorContent="Error rendering location">
                <Map center={firstVersion.location} markers={[firstVersion.location]} subscription={subscription} />
              </ErrorBoundary>
            </div>
          ) : (
            ''
          )}
          {!!comments && showCommentVersionIds.includes(firstVersion.versionId) && comments.some((x) => x.resourceId === firstVersion.versionId) && (
            <VersionComments commentThread={comments.find((x) => x.resourceId === firstVersion.versionId)} />
          )}
        </div>
        {expandedVersionGroup === versionsGroupedByAction.groupId &&
          groupedVersions.map((groupedVersion) => (
            <div key={groupedVersion.versionId} className="history-item-row">
              <div className="history-item sub-item">
                <div className="item-content">
                  <div className="item-description">
                    <Icon name="circle" />
                    <React.Fragment>
                      <span>{moment.utc(groupedVersion.createdDate).local().calendar(null, calendarOptions)}</span>
                      &nbsp;-&nbsp;
                      <Link to={`/forms/${groupedVersion.formId}?versionId=${groupedVersion.versionId}`} target="_blank">
                        Form Version {groupedVersion.versionNumber}
                      </Link>
                    </React.Fragment>
                  </div>
                </div>
                <div className="history-item-row-right-container">
                  <RevertVersion formVersion={groupedVersion} latestFormVersion={latestFormVersion} onRevertToVersion={props?.onRevertToVersion} />
                </div>
              </div>
            </div>
          ))}
      </React.Fragment>
    );
  }

  return historyItems;
}

function RevertVersion(props) {
  const { formVersion, latestFormVersion } = props;
  if (latestFormVersion?.versionId === formVersion?.versionId || !canIUse(AppFeatures.FormRevert)) {
    return <></>;
  }
  return (
    <div
      className="form-revert"
      onClick={() => {
        props?.onRevertToVersion(formVersion.versionId);
      }}
    >
      <a>Revert to this version</a>
    </div>
  );
}

function ShowComment(props) {
  const { formVersion, showCommentVersionIds, setShowCommentVersionIds } = props;
  if (!canIUse(Features.TransferComments)) {
    return null;
  }

  return (
    <div
      className="show-comment"
      onClick={() => {
        if (!showCommentVersionIds.includes(formVersion.versionId)) {
          analyticsTrack('Form History - Comment Opened');
        }

        setShowCommentVersionIds(
          showCommentVersionIds.includes(formVersion.versionId)
            ? showCommentVersionIds.filter((x) => x !== formVersion.versionId)
            : showCommentVersionIds.concat(formVersion.versionId)
        );
      }}
    >
      <a>{!showCommentVersionIds.includes(formVersion.versionId) ? 'Open' : 'Close'} Comment</a>
    </div>
  );
}

function getSourceDisplay(source) {
  switch (source) {
    case 'web':
      return 'using Web App';
    case 'ios':
      return 'using iOS App';
    case 'android':
      return 'using Android App';
    case 'windows':
      return 'using Windows App';
    case 'mobile':
      return 'using Mobile App';
    case 'system':
      return 'by System';
    default:
      return '';
  }
}

function getMergedTransferHistory(formAndTemplate, versionsGroupedByActions) {
  const transferVersions = versionsGroupedByActions
    .filter((x) => x.versionActions.indexOf('transferred') > -1 || x.versionActions.indexOf('takeownership') > -1)
    .map((x) => x.versions[0]);

  let transferHistory = formAndTemplate.form.transferHistory || [];

  transferHistory = transferHistory.slice(0, Math.max(0, transferHistory.length - transferVersions.length));
  return transferHistory;
}

function LegacyHistory(props) {
  const { formAndTemplate, getUserName, calendarOptions } = props;
  const historyItems = [];
  const subscription = useAppSelector<EditionCode>((state) => state.profile.client.subscription);
  const [showMap, setShowMap] = useState<boolean>(false);

  historyItems.push(
    <div key="created" className="history-item-row">
      <div className="history-item">
        <Icon name="file alternate outline" />
        <div className="item-content">
          <div className="item-header">Created by {getUserName(formAndTemplate.form.createdBy)}</div>
          <div className="item-description">{moment.utc(formAndTemplate.form.createdDate).local().calendar(null, calendarOptions)}</div>
        </div>
      </div>
    </div>
  );

  if (formAndTemplate.form.transferHistory && formAndTemplate.form.transferHistory.length > 0) {
    let i = 0;
    for (const item of formAndTemplate.form.transferHistory) {
      historyItems.push(
        <div key={i} className="history-item-row">
          <div className="history-item">
            <Icon name="arrow alternate circle right outline" />
            <div className="item-content">
              <div className="item-header">Transferred to {getUserName(item.destId)}</div>
              <div className="item-description">{moment.utc(item.date).local().calendar(null, calendarOptions)}</div>
            </div>
          </div>
        </div>
      );
      i++;
    }
  }

  if (formAndTemplate.form.completedDate) {
    historyItems.push(
      <div key="completed" className="history-item-row">
        <div className="history-item">
          <Icon name="check circle outline" />
          <div className="item-content">
            <div className="item-header">Completed by {getUserName(formAndTemplate.form.userId)}</div>
            <div className="item-description">{moment.utc(formAndTemplate.form.completedDate).local().calendar(null, calendarOptions)}</div>
          </div>
          <div className="history-item-row-right-container">
            {formAndTemplate.form.longitude && formAndTemplate.form.latitude ? (
              <div
                className="map-icon"
                onClick={() => {
                  setShowMap(!showMap);
                }}
              >
                <Icon name={'map outline'} />
              </div>
            ) : null}
          </div>
        </div>
        {showMap && (
          <div className="map-wrapper">
            <ErrorBoundary errorContent="Error rendering location">
              <Map
                center={{ latitude: formAndTemplate.form.latitude, longitude: formAndTemplate.form.longitude }}
                markers={[{ latitude: formAndTemplate.form.latitude, longitude: formAndTemplate.form.longitude }]}
                subscription={subscription}
              />
            </ErrorBoundary>
          </div>
        )}
      </div>
    );
  }
  return historyItems;
}

interface VersionCommentsProps {
  commentThread: CommentThread;
}

function VersionComments(props: VersionCommentsProps) {
  if (!props.commentThread) {
    return null;
  }

  const commentElements = props.commentThread.comments.map((comment, index) => (
    <>
      {index > 0 && <div className="comment-divider" />}
      <p>{comment.text}</p>
    </>
  ));

  return <div className="comments-area">{commentElements}</div>;
}
